import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      if (data?.email_sent) {
        toast.success("Message sent. We'll be in touch shortly.");
      } else {
        toast.success("Message received. Our team will respond by email.");
      }
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again or email us directly.";
      toast.error(typeof msg === "string" ? msg : "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-28 lg:py-40 border-t border-[#E0DDD6]"
    >
      {/* Section-local glow */}
      <div
        aria-hidden
        className="absolute -bottom-20 left-[-15%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(19,98,7,0.08) 0%, rgba(19,98,7,0) 60%)",
          filter: "blur(50px)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="inline-block w-8 h-px bg-[#136207]" />
          <span className="overline text-[#136207]">05 · Contact</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <h1 className="font-display text-[#1A1A1A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium">
              Get in <span className="italic">touch.</span>
            </h1>
            <p className="mt-6 text-[#5C5A55] text-base leading-relaxed font-light max-w-md">
              For partnership, capital introductions or media — write directly to the
              Meezan Ventures office.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:contact@adam-holdings.com"
                data-testid={CONTACT.emailLink}
                className="group flex items-start gap-5 border-t border-[#E0DDD6] pt-6 hover:border-[#136207] transition-colors"
              >
                <Mail className="w-4 h-4 text-[#136207] mt-1.5 shrink-0" />
                <div className="flex-1">
                  <div className="overline">Email</div>
                  <div className="text-[#1A1A1A] text-lg mt-1 group-hover:text-[#136207] transition-colors">
                    contact@adam-holdings.com
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#8A8880] group-hover:text-[#136207] transition-colors" />
              </a>

              <div className="flex items-start gap-5 border-t border-b border-[#E0DDD6] py-6">
                <span className="w-4 h-4 mt-1.5 shrink-0 inline-block bg-[#136207]" />
                <div>
                  <div className="overline">Office</div>
                  <div className="text-[#1A1A1A] text-lg mt-1">Global Investment Platform</div>
                  <div className="text-[#5C5A55] text-sm">Headquartered in the UAE</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid={CONTACT.form}
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-10 lg:border-l border-[#E0DDD6]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                testId={CONTACT.inputName}
                required
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                testId={CONTACT.inputEmail}
                required
              />
              <div className="sm:col-span-2">
                <Field
                  label="Company (optional)"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testId={CONTACT.inputCompany}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-3">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid={CONTACT.inputMessage}
                  rows={5}
                  required
                  placeholder="Tell us about your enquiry…"
                  className="w-full bg-transparent border-b border-[#E0DDD6] focus:border-[#136207] outline-none text-[#1A1A1A] text-base py-3 placeholder:text-[#8A8880] transition-colors resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid={CONTACT.submit}
              disabled={loading}
              className="mt-12 group inline-flex items-center gap-3 bg-[#136207] text-white px-8 py-5 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send Enquiry
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>

            <p className="mt-6 text-[#8A8880] text-xs">
              By submitting, you agree to be contacted by the Meezan Ventures office.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, testId, type = "text", required }) => (
  <div>
    <label className="overline block mb-3">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      required={required}
      className="w-full bg-transparent border-b border-[#E0DDD6] focus:border-[#136207] outline-none text-[#1A1A1A] text-base py-3 placeholder:text-[#8A8880] transition-colors"
      placeholder=""
    />
  </div>
);

export default Contact;
